import React, { useState } from 'react';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';
import { TextButton } from 'js/design_system/TextButton';
import ThumbUpFilled from 'js/design_system/icons/thumb_up_filled.svg';
import ThumbDownFilled from 'js/design_system/icons/thumb_down_filled.svg';
import { consoleError } from 'js/utils/console';
import { Spinner } from 'js/design_system/Spinner';
import styled from 'astroturf/react';

type Props = {
  featureName: string;
  extra?: Record<string, any>;
};

const FeatureFeedbackContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm-24);
`;

/**
 * FeatureFeedback is a component that allows users to provide feedback on a feature.
 * It can be used anywhere, in both React and Slim. It stores the feedback in the
 * `feature_feedback` table in Redshift. 1 is thumbs up, 0 is thumbs down.
 * The DB can store any integer value in that column, so it's possible that in the future
 * there will be different feedback UI components. Or if you want a different feedback UI,
 * such as a star rating, you can write a new component to do that.
 *
 * @param {string} featureName - The name of the feature to provide feedback on.
 *     You'll filter the data by this to get data relevant to your feature.
 * @returns {React.FC} - The FeatureFeedback component.
 */
export const FeatureFeedback: React.FC<Props> = ({ featureName, extra }) => {
  const [thumbsUpClicked, setThumbsUpClicked] = useState(false);
  const [thumbsDownClicked, setThumbsDownClicked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);

  const handleClick = async (buttonClicked: 'thumbs-up' | 'thumbs-down') => {
    setIsLoading(true);
    setShowSpinner(false);
    setThumbsUpClicked(false);
    setThumbsDownClicked(false);

    // Show spinner after 500ms delay
    const spinnerTimeout = setTimeout(() => {
      setShowSpinner(true);
    }, 500);

    try {
      const feedbackValue = buttonClicked === 'thumbs-up' ? 1 : 0;
      const params: Record<string, any> = {
        subdomain_param: 'api',
        feature_name: featureName,
        feedback_value: feedbackValue,
        hit_id: window.Aedu.hit_data?.id,
        auvid: window.Aedu.hit_data?.auvid,
      };

      if (extra !== undefined) {
        params.extra = JSON.stringify(extra);
      }

      const response = await fetchWithCsrf(
        routes.api_v0_feature_feedback_index_path(params),
        { method: 'POST' },
      );
      if (response.ok) {
        buttonClicked === 'thumbs-up'
          ? setThumbsUpClicked(true)
          : setThumbsDownClicked(true);
      } else {
        consoleError('Failed to send feedback');
      }
    } catch (error) {
      consoleError(`Failed to send feedback: ${error}`);
    } finally {
      clearTimeout(spinnerTimeout);
      setIsLoading(false);
      setShowSpinner(false);
    }
  };

  const getIcon = (buttonType: 'thumbs-up' | 'thumbs-down') => {
    if (isLoading && showSpinner) {
      return <Spinner size={20} />;
    }

    if (buttonType === 'thumbs-up') {
      return thumbsUpClicked ? <ThumbUpFilled size={20} /> : 'thumb_up';
    } else {
      return thumbsDownClicked ? <ThumbDownFilled size={20} /> : 'thumb_down';
    }
  };

  return (
    <FeatureFeedbackContainer>
      <TextButton
        id="feature-feedback-thumbs-up"
        onClick={async () => await handleClick('thumbs-up')}
        icon={getIcon('thumbs-up')}
        inline
        size="sm"
        disabled={isLoading}
      >
        Helpful
      </TextButton>
      <TextButton
        id="feature-feedback-thumbs-down"
        onClick={async () => await handleClick('thumbs-down')}
        icon={getIcon('thumbs-down')}
        inline
        size="sm"
        disabled={isLoading}
      >
        Not Helpful
      </TextButton>
    </FeatureFeedbackContainer>
  );
};
